'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@repo/tanstack/query';
import { useDeviceLocation } from './device-location.tsx';
import type { Coordinates } from './google-maps-input.ts';
export type LocationBias = {
  coordinates: Coordinates;
  /** Metres. Google ignores a location bias without a radius. */
  radius: number;
};
export const UK_LOCATION_BIAS: LocationBias = {
  coordinates: {
    lat: 54,
    lng: -2.5
  },
  radius: 500_000
};
const NEARBY_BIAS_RADIUS = 50_000;
type StopWithPlace = {
  place?: null | {
    lat?: null | number | string;
    lng?: null | number | string;
  };
};

/** Blank strings are missing coordinates, not 0. */
const toCoordinate = (value: null | number | string | undefined) => typeof value === 'string' && value.trim() === '' ? Number.NaN : Number(value ?? Number.NaN);
const getStopCoordinates = (stop: StopWithPlace | undefined): Coordinates | undefined => {
  const lat = toCoordinate(stop?.place?.lat);
  const lng = toCoordinate(stop?.place?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? {
    lat,
    lng
  } : undefined;
};

/** The Stop before `index` in Route order, else the first other Stop with coordinates. */
export const getRouteStopBiasCoordinates = (stops: StopWithPlace[], index: number) => {
  const precedingStop = getStopCoordinates(stops[index - 1]);
  if (precedingStop) return precedingStop;
  for (const [stopIndex, stop] of stops.entries()) {
    const coordinates = stopIndex === index ? undefined : getStopCoordinates(stop);
    if (coordinates) return coordinates;
  }
  return undefined;
};
export const useNearbyBias = t0 => {
  const $ = _c(8);
  const {
    enabled,
    stopCoordinates
  } = t0;
  const deviceLocation = useDeviceLocation();
  let t1;
  if ($[0] !== deviceLocation.key) {
    t1 = ["device-location", deviceLocation.key];
    $[0] = deviceLocation.key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== deviceLocation.source || $[3] !== enabled || $[4] !== t1) {
    t2 = {
      enabled,
      gcTime: Number.POSITIVE_INFINITY,
      queryFn: deviceLocation.source,
      queryKey: t1,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[2] = deviceLocation.source;
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: deviceCoordinates
  } = useQuery(t2);
  const coordinates = deviceCoordinates ?? stopCoordinates;
  let t3;
  if ($[6] !== coordinates) {
    t3 = coordinates ? {
      coordinates,
      radius: NEARBY_BIAS_RADIUS
    } : UK_LOCATION_BIAS;
    $[6] = coordinates;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};